import { useMemo, useState } from 'react';

type UsePaginationOptions = {
  itemsPerPage?: number;
};

export const usePagination = <T>(items: T[], options?: UsePaginationOptions) => {
  const itemsPerPage = options?.itemsPerPage || 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  // Get current page items
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, itemsPerPage]);

  // Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Reset page when items change
  useMemo(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [items.length, currentPage, totalPages]);

  return {
    paginatedItems,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    shouldShowPagination: totalPages > 1,
    goToPage,
    goToNextPage,
    goToPreviousPage,
  };
};
